// src/components/dashboard/MunicipalityDashboard.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847',
  cream: '#F0EFEB', warm: '#F7F6F3', sand: '#E2E0DB', white: '#FFFFFF',
  green: '#2E7D32', greenBg: '#e8f5e9', red: '#c0392b', redBg: '#fde8e8',
  blue: '#1565C0', blueBg: '#e3f2fd',
};

const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const SERIF = "Georgia, serif";

const APP_STATUS_CONFIG = {
  draft:              { label: 'Draft',               color: '#6b7280' },
  questionnaire_done: { label: 'Questionnaire Done',  color: '#92400e' },
  in_progress:        { label: 'In Progress',         color: '#065f46' },
  under_review:       { label: 'Under Review',        color: '#1e40af' },
  revision_needed:    { label: 'Revision Needed',     color: '#9a3412' },
  submitted_to_ded:   { label: 'Submitted to DED',    color: '#1e40af' },
  approved:           { label: 'Approved',            color: '#065f46' },
  denied:             { label: 'Denied',              color: '#991b1b' },
  withdrawn:          { label: 'Withdrawn',           color: '#6b7280' },
  completed:          { label: 'Completed',           color: '#065f46' },
};

export default function MunicipalityDashboard() {
  const { profile } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.municipalities?.id) fetchApplications();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchApplications() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('id, project_name, status')
        .eq('municipality_id', profile.municipalities.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setApplications(data || []);
    } catch (err) {
      console.error('Error fetching applications:', err.message);
      setApplications([]);
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: C.slate, fontFamily: SANS }}>
        Loading your applications...
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', maxWidth: 480, margin: '0 auto', fontFamily: SANS }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📂</div>
        <h3 style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 700, color: C.navy, margin: '0 0 10px' }}>
          No applications yet
        </h3>
        <p style={{ fontSize: 14, color: C.slate, lineHeight: 1.6, margin: '0 0 20px' }}>
          Your community does not have any CDBG applications on file. Contact PADD staff to discuss your project and get started.
        </p>
        <a
          href="mailto:padd@nepadd.com"
          style={{
            display: 'inline-block', padding: '10px 24px', borderRadius: 8,
            background: C.gold, color: '#fff', fontWeight: 600, fontSize: 14,
            textDecoration: 'none',
          }}
        >
          Contact PADD Staff
        </a>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 700, color: C.navy, marginBottom: 16 }}>
        Your Applications
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {applications.map(app => {
          const statusConfig = APP_STATUS_CONFIG[app.status] || APP_STATUS_CONFIG.draft;
          return (
            <Link
              key={app.id}
              to={`/apply/${app.id}`}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 18px', backgroundColor: C.white, borderRadius: 10,
                border: `1px solid ${C.sand}`, textDecoration: 'none',
                color: C.navy, fontWeight: 600, transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)')}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
            >
              <span style={{ fontSize: 15 }}>{app.project_name || 'Untitled Application'}</span>
              <span style={{
                padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                color: statusConfig.color, border: `1px solid ${statusConfig.color}`,
                flexShrink: 0, marginLeft: 12,
              }}>
                {statusConfig.label}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
